import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import Loader from "../components/loader.tsx";
import { Box, HStack, Stack, Table } from "@chakra-ui/react";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { formatTimestampToDate } from "../utils/mappers/timestamp.mapper.ts";
import { Button } from "../components/ui/button.tsx";
import { FaPlus } from "react-icons/fa";
import { usePagination } from "../hooks";

export const WarehouseEntriesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const pageSize = 10;

  const {
    pageIndex: pageEntryIndex,
    setPage,
    nextPage: onNextEntryPage,
    previousPage: onPreviousEntryPage,
    canPreviousPage: canPreviousEntryPage,
  } = usePagination({
    pageType: "entries",
    id: id!,
    pageSize,
  });

  const { data: entries, isLoading: isLoadingEntries } = useQuery(
    ["entries", id, pageEntryIndex, pageSize],
    () =>
      api.codebookEntry.getCodebookEntriesByWarehouseId(
        id!,
        pageSize,
        pageEntryIndex * pageSize,
      ),
    {
      keepPreviousData: false,
    },
  );

  // Calculate canNextPage based on data
  const canNextEntryPage = entries
    ? pageEntryIndex < Number(entries?.totalPages) - 1
    : false;

  if (isLoadingEntries) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      pt="5.25rem"
      p="1rem"
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <HStack
        w="full"
        justify="space-between"
        pt="5.25rem"
        mb="1rem"
        flexWrap="wrap"
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          PREGLED ULAZA MATERIJALA
        </Box>
        <Button
          size={"sm"}
          colorPalette={"red"}
          variant={"outline"}
          color={"#000000"}
          backgroundColor={"#ffffff"}
          borderColor={"#ffffff"}
          fontWeight={"600"}
          fontSize={"14px"}
          borderRadius={"0.5rem"}
          onClick={() => navigate(`create`)}
        >
          <FaPlus /> Dodaj ulaz
        </Button>
      </HStack>
      <Stack width="full" gap="5">
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                <Table.ColumnHeader
                  key={1}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  BROJ FAKTURE
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={2}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  DATUM UVOZA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={3}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  DOBAVLJAC
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  POREKLO
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={5}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  DATUM IZDAVANJA FAKTURE
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={6}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  CENA PO FAKTURI
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={7}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  KOLICINA
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {entries?.items?.map((item) => (
                <Table.Row
                  key={item.id}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.documentNumber}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {formatTimestampToDate(item.purchaseDate)}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.supplierName}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.origin}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {formatTimestampToDate(item.purchaseDate)}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {`${item.purchasePrice}€`}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.quantity}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <CustomPagination
            count={entries!.totalItems}
            pageSize={10}
            currentPage={pageEntryIndex}
            onPageChange={setPage}
            onNextPage={onNextEntryPage}
            onPreviousPage={onPreviousEntryPage}
            canNextPage={canNextEntryPage}
            canPreviousPage={canPreviousEntryPage}
            siblingCount={1}
          />
        </Box>
      </Stack>
    </Box>
  );
};
